import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, FileSpreadsheet, FileText, Search, Loader2, ChevronLeft, ChevronRight, X } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { STATUSES } from "@/lib/statuses";
import { fmtDate, copyText } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/tracking/StatusBadge";
import { PageTitle } from "@/components/admin/AdminLayout";

const KEYS = ["q", "status", "destination", "date_from", "date_to", "archived"];

function Filters({ params, setParam, reset }) {
  return (
    <div className="bg-white border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3" data-testid="shipments-filters">
      <div className="relative lg:col-span-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input defaultValue={params.q} key={params.q} onKeyDown={(e) => e.key === "Enter" && setParam("q", e.currentTarget.value)} onBlur={(e) => setParam("q", e.target.value)} placeholder="Codice, mittente o destinatario" className="pl-9 rounded-none" data-testid="filter-search-input" />
      </div>
      <Select value={params.status || "ALL"} onValueChange={(v) => setParam("status", v === "ALL" ? "" : v)}>
        <SelectTrigger className="rounded-none" data-testid="filter-status-select"><SelectValue placeholder="Stato" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">Tutti gli stati</SelectItem>
          <SelectItem value="DELAYED">In ritardo</SelectItem>
          {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input defaultValue={params.destination} key={`d-${params.destination}`} onBlur={(e) => setParam("destination", e.target.value)} onKeyDown={(e) => e.key === "Enter" && setParam("destination", e.currentTarget.value)} placeholder="Destinazione" className="rounded-none" data-testid="filter-destination-input" />
      <div className="flex gap-2 lg:col-span-2">
        <Input type="date" value={params.date_from} onChange={(e) => setParam("date_from", e.target.value)} className="rounded-none" title="Affidata dal" data-testid="filter-date-from" />
        <Input type="date" value={params.date_to} onChange={(e) => setParam("date_to", e.target.value)} className="rounded-none" title="Affidata al" data-testid="filter-date-to" />
      </div>
      <div className="flex flex-wrap items-center gap-2 lg:col-span-6">
        {[["false", "Attive"], ["true", "Archiviate"], ["all", "Tutte"]].map(([v, l]) => (
          <button key={v} onClick={() => setParam("archived", v)} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${params.archived === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-900"}`} data-testid={`filter-archived-${v}`}>{l}</button>
        ))}
        <button onClick={reset} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900" data-testid="filter-reset-btn"><X className="h-3.5 w-3.5" /> Azzera filtri</button>
      </div>
    </div>
  );
}

export default function Shipments() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const params = Object.fromEntries(KEYS.map((k) => [k, sp.get(k) || (k === "archived" ? "false" : "")]));
  const page = Number(sp.get("page") || 1);
  const [data, setData] = useState(null);
  const query = sp.toString();

  const apiParams = useCallback(() => {
    const p = { archived: params.archived };
    KEYS.forEach((k) => params[k] && (p[k] = params[k]));
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    setData(null);
    api.get("/admin/shipments", { params: { ...apiParams(), page, limit: 20 } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [query, page, apiParams]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    next.delete("page");
    if (next.toString() !== sp.toString()) setSp(next);
  };
  const exportAs = async (format) => {
    try {
      await downloadFile("/admin/shipments/export", { ...apiParams(), format }, `spedizioni.${format}`);
    } catch (e) {
      toast.error("Esportazione non riuscita");
    }
  };
  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div data-testid="admin-shipments">
      <PageTitle title="Spedizioni" subtitle={data ? `${data.total} risultati` : "Caricamento..."}>
        <Button variant="outline" className="rounded-none" onClick={() => exportAs("csv")} data-testid="export-csv-btn"><FileText className="h-4 w-4 mr-1" /> CSV</Button>
        <Button variant="outline" className="rounded-none" onClick={() => exportAs("xlsx")} data-testid="export-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
        <Button className="rounded-none bg-[#E50914] hover:bg-[#C10710]" onClick={() => navigate("/admin/spedizioni/nuova")} data-testid="admin-shipment-create-btn"><Plus className="h-4 w-4 mr-1" /> Nuova</Button>
      </PageTitle>
      <Filters params={params} setParam={setParam} reset={() => setSp(new URLSearchParams())} />
      <div className="mt-4 bg-white border border-slate-200 overflow-x-auto">
        {!data ? <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#E50914]" /></div> : data.items.length === 0 ? (
          <p className="p-10 text-center text-slate-500" data-testid="shipments-empty">Nessuna spedizione trovata. <Link to="/admin/spedizioni/nuova" className="text-[#E50914] font-semibold">Crea una spedizione</Link></p>
        ) : (
          <table className="w-full text-sm min-w-[860px]" data-testid="shipments-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">Codice</th><th className="py-3">Destinatario</th><th className="py-3">Tratta</th><th className="py-3">Affidata</th><th className="py-3">Prevista</th><th className="py-3">Stato</th><th className="py-3 pr-4"></th>
            </tr></thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.code} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`shipment-row-${s.code}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/spedizioni/${s.code}`} className="font-mono font-bold hover:text-[#E50914]" data-testid={`shipment-link-${s.code}`}>{s.code}</Link>
                      <button onClick={() => copyText(s.code, "Codice copiato")} className="text-slate-400 hover:text-slate-900" aria-label="Copia codice" data-testid={`copy-code-${s.code}`}><Copy className="h-3.5 w-3.5" /></button>
                    </div>
                    {s.archived && <span className="text-[10px] uppercase tracking-wider text-slate-400">Archiviata</span>}
                  </td>
                  <td className="py-3">{s.recipient_name}</td>
                  <td className="py-3 text-slate-600">{s.origin_city} → {s.destination_city}</td>
                  <td className="py-3">{fmtDate(s.pickup_date)}</td>
                  <td className="py-3">{fmtDate(s.expected_delivery)}</td>
                  <td className="py-3"><StatusBadge status={s.status} /></td>
                  <td className="py-3 pr-4 text-right"><Link to={`/admin/spedizioni/${s.code}`} className="text-xs font-semibold uppercase tracking-wider text-[#E50914] hover:underline" data-testid={`open-shipment-${s.code}`}>Gestisci</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && pages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm" data-testid="shipments-pagination">
          <Button variant="outline" size="sm" className="rounded-none" disabled={page <= 1} onClick={() => { const n = new URLSearchParams(sp); n.set("page", page - 1); setSp(n); }} data-testid="page-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <span>Pagina {page} di {pages}</span>
          <Button variant="outline" size="sm" className="rounded-none" disabled={page >= pages} onClick={() => { const n = new URLSearchParams(sp); n.set("page", page + 1); setSp(n); }} data-testid="page-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
